import type { IMultiEntityLinkDataSource } from "@/data/dao/service/BaseServiceDao";
import type { ILinkableEntity } from "@/data/model/Shared";
import { Format } from "@/data/util/Format";



import { translations } from "@/shared/localization/translations";


export interface IMultiEntityInputConfig {
    dataSource: IMultiEntityLinkDataSource;
    format?: { (m: any): string };
    // additional css class applied to the label of an entity, both in the search results and when selected
    cssClass?: { (m: any): string | undefined };
    // number of options visible at once in the dropdown, the rest is scrollable
    maxVisibleOptions?: number;
}

export class MultiEntityInput {
    private $element: JQuery;
    private $input?: JQuery;
    private $search: JQuery;

    private selectedIds: Array<string>;
    private selectedEntities: Array<ILinkableEntity>;

    private runningSearchXhr?: Promise<any>;
    private searchTypeDelay?: number;
    private closeDelay?: number;

    private config: IMultiEntityInputConfig;
    private mouseDownOnSearch: boolean = false;

    public onChange?: { (value: Array<string>) };

    constructor(config: IMultiEntityInputConfig, $input?: JQuery) {
        this.$element = $('<div class="multientityinput multi-entity-input" />');

        this.config = config;

        if ($input === undefined)
            $input = $(
                '<input type="text" class="form-comtrol" placeholder="' + translations.general.searchTerm + '" />'
            ).appendTo(this.$element);
        else {
            const parent = $input.attr("placeholder", translations.general.searchTerm).parent();

            const indexOfInput = $input.index();
            this.$input = $input.detach().appendTo(this.$element);

            if (indexOfInput === 0) this.$element.prependTo(parent);
            else this.renderTo(parent);
        }

        this.$input!.focus(() => this.openSearch())
            .blur(() => {
                if (this.mouseDownOnSearch) {
                    console.log("Do not close search, still focused");
                    return;
                }

                if (typeof this.closeSearch === "function") {
                    this.closeSearch();
                }
            })
            .keyup(() => {
                if (this.searchTypeDelay !== undefined) clearTimeout(this.searchTypeDelay);

                this.searchTypeDelay = window.setTimeout(() => {
                    this.updateSearch(this.$input!.val().length === 0 ? undefined : this.$input!.val());
                    this.searchTypeDelay = undefined;
                }, 320);
            });

        this.$search = $('<div class="search" />').appendTo(this.$element);

        this.selectedEntities = new Array<ILinkableEntity>();
        this.selectedIds = new Array<string>();
    }

    public getValue(): Array<string> {
        return this.selectedIds;
    }

    public setValue(value?: Array<ILinkableEntity>): MultiEntityInput {
        this.selectedEntities = new Array<ILinkableEntity>();
        this.selectedIds = new Array<string>();

        this.$element.find("> .link").remove();

        if (value !== undefined) {
            for (const val of value) {
                this.add(val);
            }
            if (value.length == 0) {
                this.notifyChange();
            }
        } else {
            this.notifyChange();
        }

        return this;
    }

    private openSearch() {
        this.$search
            .stop(true, true)
            .css("top", this.$element.height() + 3)
            .css("max-height", this.config.maxVisibleOptions === undefined ? "" : this.config.maxVisibleOptions * 34 + 2)
            .slideDown("fast", "easeOutExpo")[0].offsetTop;
        this.$element.addClass("open");

        this.$search
            .mousedown(() => {
                this.mouseDownOnSearch = true;
            })
            .mouseout(() => {
                this.mouseDownOnSearch = false;
            })
            .mouseup(() => {
                this.mouseDownOnSearch = false;
            });
        this.updateSearch(undefined);

        if (this.closeDelay !== undefined) {
            clearTimeout(this.closeDelay);
            (this as any).closeSearch = undefined;
        }
    }

    private closeSearch(ignoreDelay?: boolean) {
        // if (this.runningSearchXhr !== undefined && !this.runningSearchXhr.isFulfilled) {
        //     this.runningSearchXhr.cancel();
        //     this.runningSearchXhr = undefined;
        // }

        if (this.closeDelay !== undefined) clearTimeout(this.closeDelay);

        if (!this.$element.hasClass("open")) return;

        this.closeDelay = window.setTimeout(
            () => {
                this.$input!.val("");
                this.$search.stop(true, false).slideUp("fast", "easeOutExpo", () => {
                    this.$search.removeAttr("style");
                })[0].offsetTop;
                this.$element.removeClass("open");
                this.closeDelay = undefined;
            },
            ignoreDelay !== undefined && ignoreDelay === true ? 0 : 120
        );
    }

    private updateSearch(term?: string) {
        this.$search
            .empty()
            .append(
                '<div class="loading"><div class="kt-spinner kt-spinner--sm kt-spinner--success"></div> ' +
                    translations.general.searchingResults +
                    "</div>"
            );
        this.runningSearchXhr = this.config.dataSource.search(this.selectedIds, term).then((e) => {
            this.$search.empty();

            if (e.length === 0) {
                this.$search.append('<div class="nodata">' + translations.general.noSearchResults + "</div>");
                return;
            }

            for (const i in e) {
                const searchableEntry: ILinkableEntity = e[i];

                let name: string | undefined;
                if (this.config.format !== undefined) name = this.config.format(searchableEntry);
                else name = Format.linkableEntityName(searchableEntry);

                $('<div class="option">' + name + "</div>")
                    .addClass(this.resolveCssClass(searchableEntry))
                    .data("model", searchableEntry)
                    .appendTo(this.$search)
                    .click((e) => {
                        e.preventDefault();
                        e.stopPropagation();

                        this.add($(e.currentTarget).data("model"));
                        this.$input!.val("");

                        if (this.closeDelay !== undefined) {
                            clearTimeout(this.closeDelay);
                            this.closeDelay = undefined;
                        }

                        if (typeof this.closeSearch === "function") {
                            this.closeSearch(true);
                        }
                    });
            }
        });
    }

    private add(entity: ILinkableEntity) {
        if (this.selectedIds.includes(entity.id)) return;

        this.selectedIds.push(entity.id);
        this.selectedEntities.push(entity);
        this.notifyChange();

        let name: string | undefined;
        if (this.config.format !== undefined) name = this.config.format(entity);
        else name = Format.linkableEntityName(entity);

        const link = $('<div class="link">' + name + "</div>")
            .addClass(this.resolveCssClass(entity))
            .data("model", entity)
            .insertBefore(this.$input!);
        $('<div class="remover"><i class="flaticon2-cross"></i></div>')
            .appendTo(link)
            .click((e) => {
                const cl = $(e.currentTarget).parent(".link");
                const pm = cl.data("model");

                const idx = this.selectedIds.indexOf(pm.id);
                if (idx !== -1) this.selectedIds.splice(idx, 1);

                const sltEntIdx = this.selectedEntities.indexOf(pm);
                if (sltEntIdx !== -1) this.selectedEntities.splice(sltEntIdx, 1);

                cl.remove();

                this.notifyChange();
            });
    }

    private resolveCssClass(entity: ILinkableEntity): string {
        if (this.config.cssClass === undefined) return "";

        return this.config.cssClass(entity) ?? "";
    }

    private notifyChange() {
        if (this.onChange) {
            this.onChange(this.selectedIds);
        }
    }

    public setError(hasError: boolean): MultiEntityInput {
        if (hasError) this.$element.addClass("is-invalid");
        else this.$element.removeClass("is-invalid");

        return this;
    }

    public setDisabled(isDisabled: boolean): MultiEntityInput {
        this.$input!.prop("disabled", isDisabled);

        return this;
    }

    public renderTo($element: JQuery): MultiEntityInput {
        this.$element.appendTo($element);
        return this;
    }

    public static createFromInput($input: JQuery, config: IMultiEntityInputConfig): MultiEntityInput {
        return new MultiEntityInput(config, $input);
    }
}
